import { parse } from 'papaparse';
import { z } from 'zod';
import { Entity, EntityTypeEnum } from '../../types/entity';
import { Subscription, SubscriptionV2, Firm } from '../../types/firm';
import { ManualTrialBalanceRow } from '../../types/workpaper';

/**
 * Zod schema representing a single account to import. It transforms what the user uploads via c
 */

const parseNumeric = z
    .string()
    .trim()
    .transform((v) => (v === '' ? null : isNaN(parseToCents(v)) ? null : parseToCents(v)));

const TrialBalanceRowImportSchema = z.object({
    // Code must be a string with at least 1 character
    code: z.string().trim().min(1, { message: 'A unique code is required.' }),

    // Debit TY
    debitTY: parseNumeric,

    // Credit TY
    creditTY: parseNumeric,

    // Debit LY
    debitLY: parseNumeric,

    // Credit LY
    creditLY: parseNumeric,
});

const TrialBalanceSchema = z.array(TrialBalanceRowImportSchema);

/**
 * Converts amount from cents to dollar
 * @param value Amount value
 * @returns empty string OR converted amount value
 */
function parseValue(value?: number | null): string | number {
    let res: string | number = '';
    if (value === 0) {
        res = 0;
    }
    if (value) {
        res = value / 100;
    }
    return res;
}

/**
 * Download trial balance template
 * @param rows Existing worksheet data
 */
export function downloadEmptyTrialBalanceTemplate(): void {
    const rowValues = ['"Account Code","Debit TY","Credit TY","Debit LY","Credit LY"'];

    const CSV = rowValues.join('\n');
    const file = new Blob([CSV], { type: 'text/csv' });

    const element = document.createElement('a');
    element.href = URL.createObjectURL(file);
    element.download = 'trial-balance.csv';
    document.body.appendChild(element);
    element.click();
}

/**
 * A promisified version of the PapaParse `parse` function.
 * Transforms headers into their JSON equivalent for easier manipulation.
 *
 * Header transformations:
 * - `Account Code` -> `code`
 * - `Debit TY` -> `debitTY`
 * - `Credit TY` -> `creditTY`
 * - `Debit LY` -> `debitLY`
 * - `Credit LY` -> `creditLY`
 *
 * Note: No validation is applied to the parsed data.
 *
 * @param {File|String} file - The file or CSV string to be parsed.
 * @returns {Promise<Object>} A promise that resolves to the parsed data.
 */
export function parseAsync(file: File): Promise<unknown[]> {
    return new Promise((resolve, reject) => {
        parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: function ({ data }) {
                resolve(data);
            },

            transformHeader(header) {
                switch (header) {
                    case 'Account Code':
                        return 'code';
                    case 'Debit TY':
                        return 'debitTY';
                    case 'Credit TY':
                        return 'creditTY';
                    case 'Debit LY':
                        return 'debitLY';
                    case 'Credit LY':
                        return 'creditLY';
                    default:
                        return header;
                }
            },
            error: function (error) {
                reject(error);
            },
        });
    });
}

type TBValidationResult =
    | { success: true; error: undefined; data: ManualTrialBalanceRow[] }
    | {
          success: false;
          data: undefined;
          error: {
              balance: { currentAmount: number; priorAmount: number; isBalanced: boolean };
              issues: { line: number; errors: { field: string; message: string }[] }[];
          };
      };

export function validateCsv(input: unknown): TBValidationResult {
    const { error, data } = TrialBalanceSchema.safeParse(input);
    const errors: { field: string; message: string }[][] = [];

    // Add the normal zod errors to the correct index
    error?.errors?.forEach((e) => {
        const idx = +e.path[0];
        if (typeof idx === 'number') {
            if (!errors[idx]) {
                errors[idx] = [];
            }
            errors[idx].push({ field: e.path[1]?.toString(), message: e.message });
        }
    });

    const uniqueCodes: string[] = [];

    data?.forEach((row, idx) => {
        const code = row.code.toLowerCase();
        // Check unique code
        if (uniqueCodes.includes(code)) {
            if (!errors[idx]) {
                errors[idx] = [];
            }
            errors[idx].push({
                field: 'code',
                message: `Duplicate code: ${row.code}.`,
            });
        } else {
            uniqueCodes.push(code);
        }

        // If debit ty is filled in, credit ty can't be and vice versa
        if (typeof row.debitTY === 'number' && typeof row.creditTY === 'number') {
            if (!errors[idx]) {
                errors[idx] = [];
            }
            errors[idx].push({
                field: 'currentAmount',
                message: `If the current debit value is filled in, the current credit value must be blank.`,
            });
        }

        // If debit ly is filled in, credit ly can't be and vice versa
        if (typeof row.debitLY === 'number' && typeof row.creditLY === 'number') {
            if (!errors[idx]) {
                errors[idx] = [];
            }
            errors[idx].push({
                field: 'priorAmount',
                message: `If the prior debit value is filled in, the current credit value must be blank.`,
            });
        }
    });

    let currentTotal = 0;
    let priorTotal = 0;
    // Check if it balances
    data?.forEach((row) => {
        const currentAmount = row.debitTY ?? (typeof row.creditTY === 'number' ? row.creditTY * -1 : 0);
        const priorAmount = row.debitLY ?? (typeof row.creditLY === 'number' ? row.creditLY * -1 : 0);

        currentTotal += currentAmount;
        priorTotal += priorAmount;
    });

    const isBalanced = currentTotal === 0 && priorTotal === 0;
    const isSuccess = errors.length === 0 && isBalanced;

    if (isSuccess) {
        return { success: true, data: data ?? [], error: undefined };
    }

    return {
        success: false,
        data: undefined,
        error: {
            balance: {
                currentAmount: currentTotal,
                priorAmount: priorTotal,
                isBalanced: isBalanced,
            },
            issues: errors.map((e, idx) => ({
                line: idx + 2,
                errors: e,
            })),
        },
    };
}

export function parseToCents(value: string | number): number {
    const parsedAmount = [];
    const split = value.toString().split('.');

    if (split.length === 1) {
        parsedAmount.push(value, '00');
    } else {
        const dollars = split[0];
        parsedAmount.push(dollars);
        const cents = split[1];

        // Limit the decimal place to 2
        if (cents.length === 2) {
            parsedAmount.push(cents);
        } else if (cents.length === 1) {
            parsedAmount.push(cents, '0');
        } else if (cents.length > 2) {
            parsedAmount.push(cents[0], cents[1]);
        }
    }
    return +parsedAmount.join('');
}

export function isNullOrUndefined(val: unknown): val is null | undefined {
    return val === undefined || val === null;
}

export function isAllNullOrUndefined(vals: unknown[]): vals is (null | undefined)[] {
    return vals.every((v) => v === undefined || v === null);
}

/**
 * Check if there is remaining active workpapers quota for an entity.
 * entity.remainingWorkpapers should be null for subscriptions > v1, so will resolve to true.
 * @param entity
 * @returns True if there is remaining quota else false.
 */
export function hasRemainingWorkpapers(entity: Entity): boolean {
    if (typeof entity.remainingWorkpapers === 'number') {
        return entity.remainingWorkpapers > 0;
    }
    return true;
}

/**
 * Check if there is remaining active entities quota for a firm
 *
 * @param entity
 * @param subscription
 * @returns True if there is no remaining quota else false
 */
export function noRemainingActiveEntities(firm: Firm, entity: Entity, subscription: Subscription | SubscriptionV2 | null): boolean {
    if (!subscription) return false; // subscription won't be null. Included due to legacy typescript structure.

    const remainingActiveEntities =
        firm.subscriptionVersion === 1
            ? (subscription as Subscription).activeEntities.remaining
            : (subscription as SubscriptionV2).totalActiveEntityAllocation - (subscription as SubscriptionV2).activeEntities;
    return remainingActiveEntities <= 0 && !entity.subscriptionActive && entity.type !== EntityTypeEnum.INDIVIDUAL;
}

/**
 * Check if a user can create a workpaper for the selected entity
 *
 * @param entity
 * @param subscription
 * @param isGuest
 * @returns Disabled hint if a user can't create a workpaper else undefined
 */
export function disableCreateWorkpaperHint(
    firm: Firm,
    entity: Entity,
    subscription: Subscription | SubscriptionV2 | null,
    isGuest?: boolean
): string | undefined {
    if (firm.subscriptionVersion === 1 && !hasRemainingWorkpapers(entity)) {
        return `Cannot create a new workpaper for this entity as it has reached it's workpaper quota for this annual commitment. Please contact your Firm Administrator to upgrade your firm's subscription.`;
    }

    if (noRemainingActiveEntities(firm, entity, subscription)) {
        return 'Cannot create a new workpaper for non-active entities as your firm has reached its allowance. Please contact your Firm Administrator to add more entities.';
    }

    if (entity.isArchived) {
        return 'Archived entity is in read-only mode. Please unarchive this entity before creating a new workpaper.';
    }

    if (isGuest) {
        return 'As a guest, you only have a read access to the entity.';
    }
}
